import React, { useState, useRef } from 'react';
import InputBox from '../common/InputBox';
import SelectBox from '../common/SelectBox';
import InputArea from '../common/InputArea';
import CustomPopup from '../common/CustomPopup';

const BrandInquiry = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    company: '', brand: '', person: '', email: '', phone: '', type: '', intro: '', url: '', etc: ''
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({
    company: '',
    brand: '',
    person: '',
    email: '',
    phone: '',
    type: '',
    intro: '',
  });

  const [agree, setAgree] = useState(false);
  const [agreeError, setAgreeError] = useState('');

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const inputRefs: { [key: string]: React.RefObject<HTMLDivElement> } = {
    company: useRef(null),
    brand: useRef(null),
    person: useRef(null),
    email: useRef(null),
    phone: useRef(null),
    type: useRef(null),
    intro: useRef(null),
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors: { [key: string]: string } = {
      company: '',
      brand: '',
      person: '',
      email: '',
      phone: '',
      type: '',
      intro: '',
    };

    if (!form.company.trim()) {
      newErrors.company = '업체명을 입력해주세요';
      isValid = false;
    }
    if (!form.brand.trim()) {
      newErrors.brand = '브랜드명을 입력해주세요';
      isValid = false;
    }
    if (!form.person.trim()) {
      newErrors.person = '담당자명을 입력해주세요';
      isValid = false;
    }
    if (!form.email.trim()) {
      newErrors.email = '이메일을 입력해주세요';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다';
      isValid = false;
    }
    if (!form.phone.trim()) {
      newErrors.phone = '연락처를 입력해주세요';
      isValid = false;
    }
    if (!form.type) {
      newErrors.type = '문의 유형을 선택해주세요';
      isValid = false;
    }
    if (!form.intro.trim()) {
      newErrors.intro = '브랜드 소개 또는 협업 제안 내용을 입력해주세요';
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) {
      const firstErrorField = Object.keys(newErrors).find(key => newErrors[key]);
      if (firstErrorField && inputRefs[firstErrorField]?.current) {
        setTimeout(() => {
          const current = inputRefs[firstErrorField]?.current;
          if (current) {
            current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const inputElement = current.querySelector('input, textarea, select') as HTMLInputElement;
            if (inputElement) {
              inputElement.focus();
            }
          }
        }, 100);
      }
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const isValid = validateForm();

      if (isValid) {
        setIsLoading(true);
        const payload = {
          company: form.company,
          brand: form.brand,
          manager: form.person,
          email: form.email,
          phone: form.phone,
          type: form.type,
          contents: form.intro,
          url: form.url,
          etc: form.etc
        };

        const response = await fetch("https://hook.eu2.make.com/32mrxh4fym4cevffhkeswqfnakq6rvui", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          alert("문의가 성공적으로 전송되었습니다!");
          setForm({
            company: '',
            brand: '',
            person: '',
            email: '',
            phone: '',
            type: '',
            intro: '',
            url: '',
            etc: ''
          });
          setAgree(false);
        } else {
          throw new Error('문의 전송에 실패했습니다. 다시 시도해주세요.');
        }
      }
    } catch (error) {
      alert('문의 전송 중 오류가 발생했습니다. 다시 시도해주세요.');
      console.error('Error submitting form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    // 1. 입력값 벨리데이션 먼저 체크
    const isValid = validateForm();
    if (!isValid) {
      return;
    }
    // 2. 약관동의 체크는 마지막에
    if (!agree) {
      setAlertMessage('개인정보 수집 및 이용에 동의해 주세요.');
      setAlertOpen(true);
      return;
    }
    handleSubmit(e as unknown as React.FormEvent<HTMLFormElement>);
  };

  return (
    <section className="w-full bg-white py-16 flex flex-col items-center">
      <div className="w-full max-w-[1400px] px-[30px] md:px-[180px] flex flex-col items-center">
        <h2 className="mt-[60px] md:mt-[80px] text-[26px] md:text-[40px] font-bold mb-6 leading-[140%] text-black">제휴/입점 문의하기</h2>
        <p className="mt-[10px] text-[18px] md:text-[20px] leading-[160%] font-normal text-black text-center">
          <span className="hidden md:inline">궁금한 내용이나 협업 제안이 있다면 아래 양식에 맞춰 자유롭게 남겨주세요.<br />검토 후 빠르게 연락드릴게요!</span>
          <span className="md:hidden">궁금한 내용이나 협업 제안이 있다면<br />아래 양식에 맞춰 자유롭게 남겨주세요.<br />검토 후 빠르게 연락드릴게요!</span>
        </p>
        <form 
          className="w-full flex flex-col gap-4 mt-[60px] md:mt-[100px]" 
          onSubmit={handleSubmit}>
          <div ref={inputRefs.company}>
            <InputBox
              label="업체명"
              name="company"
              required
              placeholder="업체명을 입력해주세요"
              value={form.company}
              onChange={handleChange}
              error={errors.company}
            />
          </div>
          <div ref={inputRefs.brand}>
            <InputBox
              label="브랜드명"
              name="brand"
              required
              placeholder="브랜드명을 입력해주세요"
              value={form.brand}
              onChange={handleChange}
              error={errors.brand}
            />
          </div>
          <div ref={inputRefs.person}>
            <InputBox
              label="담당자명"
              name="person"
              required
              placeholder="담당자명을 입력해주세요"
              value={form.person}
              onChange={handleChange}
              error={errors.person}
            />
          </div>
          <div ref={inputRefs.email}>
            <InputBox
              label="이메일"
              name="email"
              type="email"
              required
              placeholder="이메일을 입력해주세요"
              value={form.email}
              onChange={handleChange}
              error={errors.email}
            />
          </div>
          <div ref={inputRefs.phone}>
            <InputBox
              label="연락처"
              name="phone"
              required
              placeholder="연락처를 입력해주세요"
              value={form.phone}
              onChange={handleChange}
              error={errors.phone}
            />
          </div>
          <div ref={inputRefs.type}>
            <SelectBox
              label="문의 유형"
              name="type"
              required
              value={form.type}
              onChange={(e: { target: { name: string; value: string } }) => handleChange(e as React.ChangeEvent<HTMLSelectElement>)}
              options={[
                { value: '', label: '문의 유형을 선택해주세요' },
                { value: '입점 문의', label: '브랜드 입점 문의' },
                { value: '제휴/마케팅 협업', label: '제휴/마케팅 협업' },
                { value: '인플루언서 협업', label: '인플루언서 협업' },
                { value: '기타', label: '기타' },
              ]}
              error={errors.type}
            />
          </div>
          <div ref={inputRefs.intro}>
            <InputArea
              label="브랜드 소개 또는 협업 제안 내용"
              name="intro"
              required
              placeholder="브랜드 스토리, 주요 제품, 제안하고 싶은 협업 아이디어 등을 자유롭게 적어주세요!"
              value={form.intro}
              onChange={handleChange}
              error={errors.intro}
            />
          </div>
          <InputBox
            label="참고 사이트 URL"
            name="url"
            placeholder="참고할 URL이 있으시다면 남겨주세요"
            value={form.url}
            onChange={handleChange}
          />
          <InputBox
            label="기타 문의 사항"
            name="etc"
            placeholder="기타 궁금하신 점 있으시면 남겨주세요"
            value={form.etc}
            onChange={handleChange}
          />
          <div className="flex flex-col gap-2 mt-0">
            <label className="font-semibold text-[18px] mb-1 text-left w-full text-black">
              개인정보 수집 및 이용 동의 <span className="text-red-500">*</span>
            </label>
            <div
              className="border rounded bg-[#fafafa] p-4 text-[14px] md:text-[16px] leading-[1.6] resize-none text-black"
              style={{ height: 200, overflowY: 'auto', whiteSpace: 'pre-line' }}>
              {`주식회사 챙기고는 폰피쉬 브랜드 제휴 및 입점 상담을 위해 최소한의 개인정보를 수집하며, 수집된 정보는 아래의 목적에 따라 활용됩니다.\n\n1. 수집하는 개인정보의 항목\n- 필수 항목: 업체명, 담당자명, 이메일, 연락처, 문의 유형, 제휴 제안 내용\n- 선택 항목: 상담에 필요한 기타 정보\n\n2. 개인정보의 수집 및 이용 목적\n- 브랜드 제휴 및 입점 문의에 대한 검토 및 상담 진행\n- 서비스 관련 안내 및 공지 전달 등\n\n3. 개인정보 보유 및 이용 기간\n- 수집된 개인정보는 수집 목적이 달성되거나 제공받은 목적이 완료된 후, 당사의 내부 방침에 따라 안전하게 보관 및 파기됩니다.\n\n※ 개인정보 제공에 동의하지 않으실 경우, 상담 및 서비스 제공이 제한될 수 있습니다.`}
            </div>
            <label className="flex items-center py-5 px-5 md:px-10 mt-2 text-[16px] text-left md:text-center w-full justify-start md:justify-center text-black">
              <div className="relative mr-4 mt-1.5">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={e => {
                    setAgree(e.target.checked);
                    if (e.target.checked) setAgreeError('');
                  }}
                  className="w-6 h-6 md:w-5 md:h-5"
                style={{
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  backgroundColor: 'white',
                  border: '1px solid #D1D5DB',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'all 0.2s ease-in-out'
                }}
                />
                {agree && (
                  <div
                    className="absolute top-0 left-0 w-6 h-6 md:w-5 md:h-5 flex items-center justify-center pointer-events-none"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M20 6L9 17L4 12"
                        stroke="black"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </div>
              <span className="md:hidden text-left">위 내용을 모두 확인하였으며,<br/>개인정보 수집 및 이용에 동의합니다.</span>
              <span className="hidden md:inline text-left">위 내용을 모두 확인하였으며, 개인정보 수집 및 이용에 동의합니다.</span>
            </label>
            {agreeError && <span className="text-red-500 text-[13px] md:text-center">{agreeError}</span>}
          </div>
          <button
            type="button"
            onClick={handleButtonClick}
            disabled={isLoading}
            className={`mt-2 md:mt-2 bg-black text-white text-[20px] md:text-[20px] font-semibold px-[100px] md:px-[120px] py-[20px] md:py-[24px] rounded-full transition self-center shadow-lg mb-[60px] md:mb-[100px] relative ${
              isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:bg-gray-800'}`}
            style={{ boxShadow: '0 4px 24px 0 rgba(0,0,0,0.12), inset 0 2px 8px 0 #fff8' }}>
            {isLoading ? (
              <>
                <span className="opacity-0">문의하기 →</span>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              </>
            ) : (
              '문의하기 →'
            )}
          </button>
        </form>
        <CustomPopup
          open={alertOpen}
          title="알림"
          message={alertMessage}
          onClose={() => setAlertOpen(false)}
        />
      </div>
    </section>
  );
};

export default BrandInquiry; 